import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deleteProfilLogo,
  getProfil,
  getProfilLogoById,
  getProfilLogos,
  setProfil,
  setProfilPrimaryLogo,
  uploadProfilLogo,
} from "../../../api";
import { PROFILE_UPDATED_EVENT } from "../../../events";
import { logError } from "../../../logging/loggingService";
import { getTaxNumberError, normalizeTaxNumberInput } from "../../../utils/taxNumber";
import { getVatIdError, normalizeVatIdInput } from "../../../utils/vatId";
import { getEmailError, getPhoneError } from "../../../utils/contactValidation";

const DEFAULT_PROFILE = {
  id: "",
  companyName: "",
  taxId: "",
  economicId: "",
  vat: "",
  email: "",
  paypalAddress: "",
  phone: "",
  addresses: [],
  bankConnections: [],
  kleinunternehmer: false,
  istVersteuerung: false,
};

const revokePreviewMap = (map) => {
  Object.values(map || {}).forEach((url) => {
    if (url && url.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }
  });
};

export default function useProfileData() {
  const isMountedRef = useRef(true);
  const logoPreviewsRef = useRef({});

  const [profileData, setProfileData] = useState(DEFAULT_PROFILE);

  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logos, setLogos] = useState([]);
  const [logoPreviews, setLogoPreviews] = useState({});
  const [logoLoading, setLogoLoading] = useState(false);
  const [uploadAsPrimary, setUploadAsPrimary] = useState(false);

  const displayedLogos = useMemo(() => {
    if (logos.length < 2) return logos;
    const primaryIndex = logos.findIndex((logo) => logo.primaryLogo);
    if (primaryIndex <= 0) return logos;
    const primaryLogo = logos[primaryIndex];
    return [primaryLogo, ...logos.slice(0, primaryIndex), ...logos.slice(primaryIndex + 1)];
  }, [logos]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [addressErrors, setAddressErrors] = useState({});
  const [bankErrors, setBankErrors] = useState({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [sectionsOpen, setSectionsOpen] = useState({
    company: true,
    tax: true,
    addresses: true,
    banks: true,
  });

  const emailError = useMemo(
    () => getEmailError(profileData.email, { required: true }),
    [profileData.email]
  );

  const phoneError = useMemo(() => getPhoneError(profileData.phone), [profileData.phone]);

  const economicIdError = useMemo(() => {
    const value = (profileData.economicId || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!value) return "";
    const ok = /^DE\d{9}(-\d{5})?$/.test(value);
    return ok ? "" : "Ungültige Wirtschafts-ID (z. B. DE123456789 oder DE123456789-00001)";
  }, [profileData.economicId]);

  const taxIdError = useMemo(() => getTaxNumberError(profileData.taxId), [profileData.taxId]);
  const vatError = useMemo(() => getVatIdError(profileData.vat), [profileData.vat]);

  const loadLogos = useCallback(async () => {
    setLogoLoading(true);
    try {
      const res = await getProfilLogos();
      const meta = Array.isArray(res?.data) ? res.data : [];
      const previews = {};
      for (const logo of meta) {
        if (!logo?.id) continue;
        try {
          const blobRes = await getProfilLogoById(logo.id);
          if (blobRes.status === 200 && blobRes.data) {
            previews[logo.id] = URL.createObjectURL(blobRes.data);
          }
        } catch (err) {
          logError("Logo konnte nicht geladen werden", err, { component: "Profile" });
        }
      }
      if (!isMountedRef.current) {
        revokePreviewMap(previews);
        return;
      }
      setLogos(meta);
      setLogoPreviews((prev) => {
        revokePreviewMap(prev);
        logoPreviewsRef.current = previews;
        return previews;
      });
      const primaryId = meta.find((logo) => logo.primaryLogo)?.id || meta[0]?.id || null;
      setLogoUrl(primaryId ? previews[primaryId] || null : null);
      if (meta.length === 0) {
        setUploadAsPrimary(true);
      }
    } catch (err) {
      logError("Fehler beim Laden der Logos", err, { component: "Profile" });
    } finally {
      if (isMountedRef.current) {
        setLogoLoading(false);
      } else {
        revokePreviewMap(logoPreviewsRef.current);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    getProfil()
      .then((res) => {
        if (!isMountedRef.current) return;
        setProfileData(res.data || {});
      })
      .catch((err) => {
        logError("Fehler beim Laden der Profildaten", err, { component: "Profile" });
        setLoadError("Profil konnte nicht geladen werden.");
      })
      .finally(() => {
        if (isMountedRef.current) setLoading(false);
      });

    loadLogos();

    return () => {
      isMountedRef.current = false;
      revokePreviewMap(logoPreviewsRef.current);
    };
  }, [loadLogos]);

  const handleInputChange = (field, value) => {
    if (field === "vat") value = normalizeVatIdInput(value);
    if (field === "economicId") value = (value || "").toUpperCase().replace(/\s+/g, "");
    if (field === "taxId") value = normalizeTaxNumberInput(value);
    setProfileData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleAddressChange = (addresses) => {
    setProfileData((prev) => ({ ...prev, addresses }));
    if (Object.keys(addressErrors).length > 0) {
      setAddressErrors({});
    }
  };

  const handleBankChange = (bankConnections) => {
    setProfileData((prev) => ({ ...prev, bankConnections }));
    if (Object.keys(bankErrors).length > 0) {
      setBankErrors({});
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    let summaryMessage = "";
    if (!profileData.companyName?.trim()) {
      nextErrors.companyName = "Bitte Unternehmensname eingeben.";
      summaryMessage = summaryMessage || nextErrors.companyName;
    }
    if (emailError) {
      nextErrors.email = emailError;
      summaryMessage = summaryMessage || emailError;
    }
    if (phoneError) {
      nextErrors.phone = phoneError;
      summaryMessage = summaryMessage || phoneError;
    }
    if (economicIdError) {
      nextErrors.economicId = economicIdError;
      summaryMessage = summaryMessage || "Bitte gültige Wirtschafts-ID eingeben.";
    }
    if (taxIdError) {
      nextErrors.taxId = taxIdError;
      summaryMessage = summaryMessage || "Bitte gültige Steuernummer eingeben.";
    }
    if (vatError) {
      nextErrors.vat = vatError;
      summaryMessage = summaryMessage || "Bitte gültige Umsatzsteuer-ID eingeben.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setSnackbarMessage(summaryMessage || "Bitte prüfe die markierten Felder.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    setFieldErrors({});
    setAddressErrors({});
    setBankErrors({});
    try {
      const normalizedProfile = {
        ...profileData,
        bankConnections: (profileData.bankConnections || []).map((bank) => ({
          ...bank,
          iban: bank.iban ? bank.iban.replace(/\s+/g, "").toUpperCase() : bank.iban,
          bic: bank.bic ? bank.bic.replace(/\s+/g, "").toUpperCase() : bank.bic,
        })),
      };
      await setProfil(normalizedProfile);
      setSnackbarMessage("Profil erfolgreich gespeichert!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT));
    } catch (error) {
      logError("Fehler beim Speichern des Profils", error, { component: "Profile" });
      const backendError = error?.response?.data;
      const details = Array.isArray(backendError?.details) ? backendError.details : null;
      if (details && details.length > 0) {
        const fieldValidationErrors = {};
        const addressValidationErrors = {};
        const bankValidationErrors = {};
        details.forEach((detail) => {
          const rawField = detail?.field;
          const message = detail?.message;
          if (!rawField || !message) return;
          const addressMatch = /^addresses\[(\d+)\]\.(.+)$/.exec(rawField);
          if (addressMatch) {
            const index = Number(addressMatch[1]);
            const field = addressMatch[2];
            addressValidationErrors[index] = {
              ...(addressValidationErrors[index] || {}),
              [field]: message,
            };
            return;
          }
          const bankMatch = /^bankConnections\[(\d+)\]\.(.+)$/.exec(rawField);
          if (bankMatch) {
            const index = Number(bankMatch[1]);
            const field = bankMatch[2];
            bankValidationErrors[index] = {
              ...(bankValidationErrors[index] || {}),
              [field]: message,
            };
            return;
          }
          fieldValidationErrors[rawField] = message;
        });
        setFieldErrors(fieldValidationErrors);
        setAddressErrors(addressValidationErrors);
        setBankErrors(bankValidationErrors);
        setSnackbarMessage(backendError?.message || "Bitte prüfe die markierten Felder.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      setSnackbarMessage("Fehler beim Speichern des Profils");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setSnackbarMessage("Nur PNG oder JPEG sind erlaubt.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    const MAX_SIZE_BYTES = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      setSnackbarMessage("Datei ist zu gross (max. 2 MB).");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("primary", uploadAsPrimary ? "true" : "false");
    try {
      setLogoUploading(true);
      await uploadProfilLogo(formData);
      await loadLogos();
      setUploadAsPrimary(false);
      setSnackbarMessage("Logo erfolgreich hochgeladen");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      logError("Fehler beim Hochladen des Logos", err, { component: "Profile" });
      setSnackbarMessage("Fehler beim Hochladen des Logos");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSetPrimaryLogo = async (logoId) => {
    try {
      await setProfilPrimaryLogo(logoId);
      await loadLogos();
      setSnackbarMessage("Standardlogo aktualisiert");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      logError("Standardlogo konnte nicht gesetzt werden", err, { component: "Profile" });
      setSnackbarMessage("Standardlogo konnte nicht gesetzt werden");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteLogo = async (logoId) => {
    try {
      await deleteProfilLogo(logoId);
      await loadLogos();
      setSnackbarMessage("Logo entfernt");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
    } catch (err) {
      logError("Logo konnte nicht entfernt werden", err, { component: "Profile" });
      setSnackbarMessage("Logo konnte nicht entfernt werden");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const toggleSection = (key) => {
    setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const reload = () => {
    setLoading(true);
    setLoadError("");
    getProfil()
      .then((res) => {
        setProfileData(res?.data || {});
      })
      .catch((err) => {
        logError("Profil konnte nicht geladen werden", err, { component: "Profile" });
        setLoadError("Profil konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
    loadLogos();
  };

  return {
    profileData,
    logoUrl,
    logoUploading,
    logos,
    logoPreviews,
    logoLoading,
    uploadAsPrimary,
    displayedLogos,
    loading,
    loadError,
    fieldErrors,
    addressErrors,
    bankErrors,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    sectionsOpen,
    emailError,
    phoneError,
    economicIdError,
    taxIdError,
    vatError,
    setUploadAsPrimary,
    setSnackbarOpen,
    handleInputChange,
    handleAddressChange,
    handleBankChange,
    handleSubmit,
    handleLogoUpload,
    handleSetPrimaryLogo,
    handleDeleteLogo,
    toggleSection,
    reload,
  };
}
